
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { RoyaltyReport } from '@/types';
import { formatCurrency } from '@/utils/formatters';

interface RecentRoyaltyReportsProps {
  reports: RoyaltyReport[];
  onViewAll: () => void;
}

interface QuarterlyReport {
  quarter: number;
  year: number;
  totalUnitsSold: number;
  totalRoyaltyAmount: number;
  bookCount: number;
  firstReportId: string;
}

const RecentRoyaltyReports: React.FC<RecentRoyaltyReportsProps> = ({ reports, onViewAll }) => {
  const navigate = useNavigate();

  // Group reports by quarter and only include data for each specific quarter/year combination
  const quarterlyReports = React.useMemo(() => {
    if (!reports) return [];
    
    const grouped = reports.reduce((acc, report) => {
      const key = `${report.year}-Q${report.quarter}`;
      
      if (!acc[key]) {
        acc[key] = {
          quarter: report.quarter,
          year: report.year,
          totalUnitsSold: 0,
          totalRoyaltyAmount: 0,
          bookCount: 0,
          firstReportId: report.id
        };
      }
      
      // Only include this report if it matches the quarter/year exactly
      if (report.quarter === acc[key].quarter && report.year === acc[key].year) {
        acc[key].totalUnitsSold += report.unitsSold || report.salesCount || 0;
        acc[key].totalRoyaltyAmount += report.royaltyAmount || report.amount || 0;
        acc[key].bookCount += 1;
      }
      
      return acc;
    }, {} as Record<string, QuarterlyReport>);
    
    return Object.values(grouped)
      .sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year;
        return b.quarter - a.quarter;
      })
      .slice(0, 5); // Show only the 5 most recent quarters
  }, [reports]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Recent Royalty Reports</CardTitle>
          <CardDescription>Your latest royalty earnings by quarter</CardDescription>
        </div>
        <div 
          className="cursor-pointer text-royalty-600 hover:text-royalty-800"
          onClick={onViewAll}
        >
          View All
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Period</TableHead>
              <TableHead>Books</TableHead>
              <TableHead className="text-right">Units Sold</TableHead>
              <TableHead className="text-right">Royalty Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {quarterlyReports.map((quarterly) => (
              <TableRow 
                key={`${quarterly.year}-Q${quarterly.quarter}`}
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => navigate(`/author/royalties/${quarterly.firstReportId}`)}
              >
                <TableCell className="font-medium">Q{quarterly.quarter} {quarterly.year}</TableCell>
                <TableCell>{quarterly.bookCount} book{quarterly.bookCount !== 1 ? 's' : ''}</TableCell>
                <TableCell className="text-right">{quarterly.totalUnitsSold.toLocaleString()}</TableCell>
                <TableCell className="text-right">{formatCurrency(quarterly.totalRoyaltyAmount)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default RecentRoyaltyReports;
